import ChatIcon from "@mui/icons-material/Chat";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SmsIcon from "@mui/icons-material/Sms";
import { Box, ButtonBase, CircularProgress, Fade, IconButton, Typography } from "@mui/material";
import { useEffect, useRef } from "react";

import { alphaVar } from "@components/ui/colors";
import { useContactCenterTheme } from "@components/virtual-receptionist/theming/ContactCenterThemeContext";
import { ChatNotificationKind, ChatNotificationPayload } from "./chatNotificationBus";

export interface ChatToast {
  id: string;
  payload: ChatNotificationPayload;
  isClosing?: boolean;
}

const KIND_CONFIG: Record<ChatNotificationKind, { label: string; icon: typeof SmsIcon }> = {
  sms: { label: "SMS", icon: SmsIcon },
  internal: { label: "Internal", icon: ChatIcon },
  "job-chat": { label: "Job Chat", icon: ChatBubbleOutlineIcon },
  group: { label: "Group", icon: GroupsRoundedIcon },
  "group-sms": { label: "Group SMS", icon: SmsIcon },
};

interface Props {
  toast: ChatToast;
  durationMs: number;
  exitDurationMs: number;
  isOpening?: boolean;
  onOpen: (toast: ChatToast) => void;
  onClose: (id: string) => void;
  onExited: (id: string) => void;
}

export function ChatNotificationToast({ toast, durationMs, exitDurationMs, isOpening = false, onOpen, onClose, onExited }: Props) {
  const { theme } = useContactCenterTheme();
  const progressRef = useRef<HTMLDivElement>(null);

  const { kind, title, body, companyName } = toast.payload;
  const { label, icon: Icon } = KIND_CONFIG[kind] ?? KIND_CONFIG.sms;
  const accent = theme.toast.accent;

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (!progressRef.current) return;

      progressRef.current.style.transition = `width ${durationMs}ms linear`;
      progressRef.current.style.width = "0%";
    });

    return () => cancelAnimationFrame(frame);
  }, [durationMs]);

  return (
    <Fade in={!toast.isClosing} timeout={{ enter: 180, exit: exitDurationMs }} onExited={() => onExited(toast.id)}>
      <Box
        sx={{
          width: 330,
          backgroundColor: theme.toast.bg,
          borderRadius: 2.5,
          boxShadow: `0 12px 32px ${alphaVar("#000", 0.14)}`,
          overflow: "hidden",
          position: "relative",
          borderTop: `3px solid ${theme.toast.border}`,
          borderRight: `3px solid ${theme.toast.border}`,
          borderBottom: `3px solid ${theme.toast.border}`,
          borderLeft: `4px solid ${alphaVar(accent, 0.72)}`,
          animation: "chatToastVibrate 520ms ease-in-out 140ms 2",
          "@keyframes chatToastVibrate": {
            "0%": { transform: "translateX(0)" },
            "12%": { transform: "translateX(-4px)" },
            "24%": { transform: "translateX(4px)" },
            "36%": { transform: "translateX(-3px)" },
            "48%": { transform: "translateX(3px)" },
            "60%": { transform: "translateX(-2px)" },
            "72%": { transform: "translateX(2px)" },
            "100%": { transform: "translateX(0)" },
          },
          "@keyframes chatToastIconPulse": {
            "0%, 100%": { transform: "scale(1)", boxShadow: `0 0 0 0 ${alphaVar(accent, 0.34)}` },
            "50%": { transform: "scale(1.08)", boxShadow: `0 0 0 8px ${alphaVar(accent, 0)}` },
          },
        }}
      >
        <ButtonBase
          onClick={() => onOpen(toast)}
          disabled={isOpening}
          aria-label={`Open conversation with ${title}`}
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            textAlign: "left",
            px: 1.75,
            py: 1.35,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box
            sx={{
              mt: 0.25,
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: alphaVar(accent, 0.12),
              color: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              animation: "chatToastIconPulse 900ms ease-out",
            }}
          >
            {isOpening ? <CircularProgress size={14} sx={{ color: accent }} /> : <Icon sx={{ fontSize: 17 }} />}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pr: 2.75 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.86rem",
                  fontWeight: 700,
                  color: theme.toast.title,
                  lineHeight: 1.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: accent,
                  textTransform: "uppercase",
                  letterSpacing: 0.35,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {label}
              </Typography>
            </Box>

            {companyName && (
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: theme.toast.muted,
                  lineHeight: 1.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                For: {companyName}
              </Typography>
            )}

            <Typography
              sx={{
                mt: 0.35,
                fontSize: "0.78rem",
                color: theme.toast.body,
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {body}
            </Typography>
          </Box>
        </ButtonBase>

        <IconButton
          size="small"
          aria-label="Dismiss notification"
          onClick={(event) => {
            event.stopPropagation();
            onClose(toast.id);
          }}
          sx={{
            position: "absolute",
            top: 7,
            right: 7,
            width: 22,
            height: 22,
            color: alphaVar(theme.toast.title, 0.55),
            backgroundColor: "transparent",
            "&:hover": {
              color: theme.toast.title,
              backgroundColor: alphaVar(theme.toast.muted, 0.12),
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>

        <Box
          ref={progressRef}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 1.5,
            width: "100%",
            backgroundColor: alphaVar(accent, 0.65),
          }}
        />
      </Box>
    </Fade>
  );
}
